import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';

import { PageViewTracker } from './analytics';
import { AppLayout, ToastProvider } from './components';
import { useLicenses } from './hooks';
import {
  PrinterProvider,
  RoutesImporterState,
  routes,
  SyncShopToActiveRoute,
  useOptionalUser,
} from './utils';

export interface AppRoutesProps {
  validSession: boolean;
  // We do Partial first so Required can properly remove the
  // undefined of the routes
  routes: Required<Partial<RoutesImporterState>>['routes'];
}

export const AppRoutes = (props: AppRoutesProps) => {
  const navigate = useNavigate();
  const { user } = useOptionalUser();

  // biome-ignore lint/correctness/useExhaustiveDependencies: must run once, the cleanup removes every CapacitorApp listener, including the drawers' backButton ones
  useEffect(() => {
    CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      if (event.url) {
        const url = new URL(event.url);
        let route: string;

        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          route = `/${url.host}${url.pathname}${url.search}`;
        } else {
          route = url.pathname + url.search;
        }

        navigate(route);
      }
    });

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, []);

  const licenses = useLicenses();

  return (
    <AnimatePresence>
      <PageViewTracker key="page-tracker" />
      <Routes key="routes">
        <Route
          element={
            <ToastProvider>
              <AppLayout>
                <Outlet />
              </AppLayout>
            </ToastProvider>
          }
        >
          {props.validSession && (
            <Route
              element={
                <PrinterProvider>
                  <Outlet />
                </PrinterProvider>
              }
            >
              <Route
                path="/:shopId/*"
                element={
                  <SyncShopToActiveRoute>
                    <Outlet />
                  </SyncShopToActiveRoute>
                }
              >
                <Route path="orders/*" element={<props.routes.OrderRoutes />} />
                <Route
                  path="order-history/*"
                  element={<props.routes.OrderHistoryRoutes />}
                />

                {licenses.kitchenDisplay && (
                  <Route
                    path="kitchen-display/*"
                    element={<props.routes.KitchenDisplayRoutes />}
                  />
                )}

                <Route
                  path="printer-settings/*"
                  element={<props.routes.PrinterSettingsRoutes />}
                />
                <Route
                  path="site-configuration/*"
                  element={<props.routes.SiteConfigurationRoutes />}
                />
                {/* <Route
                  path="settings/*"
                  element={<props.routes.SettingsRoutes />}
                />
                {/* <Route path="stats/*" element={<props.routes.StatsRoutes />} /> */}
                <Route path="" element={<props.routes.OrderRoutes />} />
                <Route path="*" element={<props.routes.PageNotFoundView />} />
              </Route>

              <>
                {/*
                 * If the user is already logged in but using an old auth URL they should be
                 * properly redirected to the landing page
                 */}

                <Route
                  path={routes.auth.login}
                  element={<Navigate to={routes.orders(user!.shops[0])} />}
                />
                <Route
                  path=""
                  element={<Navigate to={routes.orders(user!.shops[0])} />}
                />
              </>
            </Route>
          )}

          {!props.validSession && (
            <>
              <Route
                path={routes.auth.login}
                element={<props.routes.LoginView />}
              />
              <Route path="*" element={<Navigate to={routes.auth.login} />} />
            </>
          )}

          <Route path={routes.unAuth} element={<props.routes.UnAuthView />} />
          <Route
            path={routes.notFound}
            element={<props.routes.PageNotFoundView />}
          />
          <Route path="*" element={<props.routes.PageNotFoundView />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
};
